import ErrorHandler from "@/lib/errorHandler";
import { save_subscription } from "@/Redux/Actions/AuthUser";
import usePageLoader from "@/Redux/hooks/usePageLoader";
import useSnackBar from "@/Redux/hooks/useSnackBar";
import React, { useEffect } from "react";
import { useAppDispatch } from "@/Redux/app/hooks";

export const VAPID_PUBLIC_KEY = process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY?.trim() || "";

const NotificationPermission: React.FC = () => {
    const { setSnackBar } = useSnackBar();
    const setFullPageLoader = usePageLoader();
    const dispatch = useAppDispatch();

    const urlBase64ToUint8Array = (base64String: string) => {
        const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
        const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
        const rawData = atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    };

    const arrayBufferToBase64 = (buffer: ArrayBuffer | null): string => {
        if (!buffer) return "";
        const bytes = new Uint8Array(buffer);
        let binary = "";
        bytes.forEach((b) => {
            binary += String.fromCharCode(b);
        });
        return window.btoa(binary);
    };

    const saveSubscription = async () => {
        if (!VAPID_PUBLIC_KEY) {
            return;
        }

        try {
            setFullPageLoader(true);
            await navigator.serviceWorker.register("/firebase-messaging-sw.js");
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
            });

            const subscriptionPayload = {
                endpoint: subscription.endpoint,
                expirationTime: subscription.expirationTime,
                keys: {
                    p256dh: arrayBufferToBase64(subscription.getKey("p256dh")),
                    auth: arrayBufferToBase64(subscription.getKey("auth")),
                },
            };

            const response = await dispatch(
                save_subscription({ subscription: subscriptionPayload })
            );

            const success = await ErrorHandler(response, setSnackBar);

            if (success) {
                setSnackBar("success", response?.payload?.message || "Notification subscription saved");
            } else {
                setSnackBar("error", response?.payload?.message || "Failed to save notification subscription");
            }
        } catch {
        } finally {
            setFullPageLoader(false);
        }
    };

    useEffect(() => {
        handleNotificationPermission();
    }, []);

    const handleNotificationPermission = async (): Promise<void> => {
        if (!("Notification" in window) || !("serviceWorker" in navigator)) return;

        const currentPermission = Notification.permission;
        if (currentPermission === "denied") return;

        if (currentPermission === "default") {
            sessionStorage.removeItem("push_subscribed");
        }

        const alreadySubscribed = sessionStorage.getItem("push_subscribed");
        if (alreadySubscribed && currentPermission === "granted") return;

        if (currentPermission === "granted") {
            await saveSubscription();
            sessionStorage.setItem("push_subscribed", "true");
            return;
        }

        try {
            const permission = await Notification.requestPermission();
            if (permission === "granted") {
                await saveSubscription();
                sessionStorage.setItem("push_subscribed", "true");
            }
        } catch {
        }
    };

    return null;
};

export default NotificationPermission;
